import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  ArrowLeft, 
  ExternalLink, 
  CheckCircle2, 
  Circle,
  AlertTriangle,
  Copy,
  Info,
  Shield,
  Key,
  Building2,
  FileCheck,
  Zap
} from "lucide-react";
import { toast } from "sonner";

const MetaSetupGuide = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado!");
  };

  const steps = [
    { id: 1, title: "Criar Conta de Desenvolvedor", icon: Building2 },
    { id: 2, title: "Criar App", icon: Zap },
    { id: 3, title: "Configurar Permissões", icon: Shield },
    { id: 4, title: "Verificar Negócio", icon: FileCheck },
    { id: 5, title: "Gerar Access Token", icon: Key },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            📘 Guia de Configuração Meta Ads
          </h1>
          <p className="text-gray-600">
            Siga estes passos para conectar sua conta de anúncios ao Mídia Tomahawk
          </p>
        </div>

        {/* Alerta Importante */}
        <Alert className="mb-6 border-amber-500 bg-amber-50">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Importante:</strong> Este processo é necessário apenas uma vez. 
            Após configurado, você poderá criar campanhas automaticamente pelo Mídia Tomahawk.
          </AlertDescription>
        </Alert>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8 px-4">
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center">
              <button
                onClick={() => setCurrentStep(step.id)}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  currentStep === step.id
                    ? "bg-blue-600 text-white"
                    : currentStep > step.id
                    ? "bg-green-500 text-white"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                {currentStep > step.id ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <step.icon className="w-5 h-5" />
                )}
              </button>
              <span className="text-xs mt-2 text-center max-w-[80px]">{step.title}</span>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Badge className="bg-blue-600">{currentStep}/5</Badge>
              {steps[currentStep - 1].title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            
            {/* STEP 1 */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <p className="text-gray-700">
                  Primeiro, você precisa ter uma conta de desenvolvedor no Meta (Facebook).
                </p>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold">📌 Passo a passo:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>Acesse <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">developers.facebook.com</a></li>
                    <li>Faça login com sua conta do Facebook</li>
                    <li>Clique em <strong>"Começar"</strong> ou <strong>"Get Started"</strong></li>
                    <li>Aceite os termos de uso</li>
                    <li>Confirme seu email se solicitado</li>
                  </ol>
                </div>

                <Alert className="border-blue-200 bg-blue-50">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800 text-sm">
                    Use a mesma conta do Facebook que gerencia sua página de negócios e conta de anúncios.
                  </AlertDescription>
                </Alert>

                <Button 
                  className="w-full"
                  onClick={() => window.open("https://developers.facebook.com", "_blank")}
                >
                  Abrir Meta for Developers
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {/* STEP 2 */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <p className="text-gray-700">
                  Agora vamos criar um app que permitirá o Mídia Tomahawk gerenciar seus anúncios.
                </p>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold">📌 Passo a passo:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>No painel de desenvolvedor, clique em <strong>"Criar App"</strong></li>
                    <li>Selecione o tipo: <strong>"Negócios"</strong> ou <strong>"Business"</strong></li>
                    <li>Nome do app: <strong>"Minha Empresa - Anúncios"</strong> (ou similar)</li>
                    <li>Email de contato: seu email comercial</li>
                    <li>Vincule ao seu <strong>Business Manager</strong> (se tiver)</li>
                    <li>Clique em <strong>"Criar App"</strong></li>
                  </ol>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Tipo de App</h4>
                  <p className="text-sm text-yellow-700">
                    Escolha <strong>"Negócios"</strong> para ter acesso às APIs de Marketing. 
                    Outros tipos (Consumidor, Jogos) não têm essa funcionalidade.
                  </p>
                </div>

                <Button 
                  className="w-full"
                  onClick={() => window.open("https://developers.facebook.com/apps/create/", "_blank")}
                >
                  Criar Novo App
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {/* STEP 3 */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <p className="text-gray-700">
                  Configure as permissões necessárias para gerenciar anúncios.
                </p>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold">📌 Permissões necessárias:</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 p-2 bg-white rounded border">
                      <Badge variant="outline">ads_management</Badge>
                      <span className="text-sm text-gray-600">Criar e gerenciar campanhas</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-white rounded border">
                      <Badge variant="outline">ads_read</Badge>
                      <span className="text-sm text-gray-600">Ler métricas e insights</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-white rounded border">
                      <Badge variant="outline">pages_read_engagement</Badge>
                      <span className="text-sm text-gray-600">Acessar páginas para criativos</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold">📌 Como solicitar:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>No seu app, vá em <strong>"Configurações do App"</strong> → <strong>"Básico"</strong></li>
                    <li>Adicione <strong>URL da Política de Privacidade</strong> (pode ser a do seu site)</li>
                    <li>Vá em <strong>"Permissões e Recursos"</strong></li>
                    <li>Procure cada permissão acima e clique em <strong>"Solicitar"</strong></li>
                    <li>Para <strong>ads_management</strong>, você precisará do App Review</li>
                  </ol>
                </div>

                <Alert className="border-red-200 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 text-sm">
                    <strong>Atenção:</strong> A permissão <code>ads_management</code> requer aprovação do Meta. 
                    O processo leva de 2-15 dias úteis.
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* STEP 4 */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <p className="text-gray-700">
                  O Meta exige verificação do seu negócio para liberar recursos avançados.
                </p>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold">📌 Documentos necessários:</h4>
                  <ul className="list-disc list-inside space-y-2 text-sm">
                    <li><strong>CNPJ</strong> - Cartão CNPJ ou Contrato Social</li>
                    <li><strong>Comprovante de endereço</strong> - Conta de luz, água ou telefone</li>
                    <li><strong>Domínio verificado</strong> - Seu site deve estar no Business Manager</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold">📌 Passo a passo:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>Acesse <a href="https://business.facebook.com/settings/security" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">business.facebook.com/settings/security</a></li>
                    <li>Clique em <strong>"Verificação da empresa"</strong></li>
                    <li>Siga as instruções para enviar documentos</li>
                    <li>Aguarde a análise (1-5 dias úteis)</li>
                  </ol>
                </div>

                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800 text-sm">
                    <strong>Dica:</strong> Se você já usa o Gerenciador de Anúncios do Facebook, 
                    provavelmente sua empresa já está verificada!
                  </AlertDescription>
                </Alert>

                <Button 
                  className="w-full"
                  onClick={() => window.open("https://business.facebook.com/settings/security", "_blank")}
                >
                  Verificar Meu Negócio
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {/* STEP 5 */}
            {currentStep === 5 && (
              <div className="space-y-4">
                <p className="text-gray-700">
                  Agora vamos gerar o Access Token que você usará no Mídia Tomahawk.
                </p>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold">📌 Passo a passo:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>No seu app, vá em <strong>"Ferramentas"</strong> → <strong>"Graph API Explorer"</strong></li>
                    <li>Selecione seu app no dropdown</li>
                    <li>Clique em <strong>"Gerar Token de Acesso"</strong></li>
                    <li>Marque as permissões: <code>ads_management</code>, <code>ads_read</code></li>
                    <li>Clique em <strong>"Gerar"</strong></li>
                    <li><strong>Importante:</strong> Converta para token de longa duração!</li>
                  </ol>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 mb-2">⏰ Token de Longa Duração</h4>
                  <p className="text-sm text-yellow-700 mb-2">
                    Tokens padrão expiram em 1-2 horas. Para uso em produção, converta para token de longa duração (60 dias):
                  </p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-yellow-700">
                    <li>Vá em <strong>"Ferramentas"</strong> → <strong>"Token de Acesso Debugger"</strong></li>
                    <li>Cole seu token e clique em <strong>"Depurar"</strong></li>
                    <li>Clique em <strong>"Estender Token de Acesso"</strong></li>
                    <li>Copie o novo token gerado</li>
                  </ol>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">📋 Dados para o Mídia Tomahawk:</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center p-2 bg-white rounded border">
                      <span><strong>Access Token:</strong> EAAxxxxxxx...</span>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard("Cole seu token aqui")}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-white rounded border">
                      <span><strong>Ad Account ID:</strong> act_123456789</span>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard("act_")}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-white rounded border">
                      <span><strong>Page ID:</strong> 123456789 (opcional)</span>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard("")}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800 text-sm">
                    <strong>Pronto!</strong> Com esses dados, você pode conectar sua conta no Mídia Tomahawk 
                    e começar a criar campanhas automaticamente!
                  </AlertDescription>
                </Alert>

                <Button 
                  className="w-full"
                  onClick={() => window.open("https://developers.facebook.com/tools/explorer/", "_blank")}
                >
                  Abrir Graph API Explorer
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
          >
            ← Anterior
          </Button>
          
          {currentStep < 5 ? (
            <Button
              onClick={() => setCurrentStep(Math.min(5, currentStep + 1))}
            >
              Próximo →
            </Button>
          ) : (
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => navigate(-1)}
            >
              ✅ Concluído - Voltar
            </Button>
          )}
        </div>

        {/* FAQ Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>❓ Perguntas Frequentes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold">Quanto tempo demora o App Review?</h4>
              <p className="text-sm text-gray-600">Geralmente 2-15 dias úteis. Tenha paciência!</p>
            </div>
            <div>
              <h4 className="font-semibold">Posso usar em modo Development?</h4>
              <p className="text-sm text-gray-600">Sim, mas com limitações. Você só consegue testar com contas vinculadas ao seu app.</p>
            </div>
            <div>
              <h4 className="font-semibold">O token expirou, o que fazer?</h4>
              <p className="text-sm text-gray-600">Gere um novo token de longa duração e atualize no Mídia Tomahawk.</p>
            </div>
            <div>
              <h4 className="font-semibold">Minha campanha foi rejeitada pelo Meta, por quê?</h4>
              <p className="text-sm text-gray-600">Todas as campanhas passam pela revisão do Meta. Verifique se seu anúncio segue as políticas de publicidade.</p>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default MetaSetupGuide;
